// React
import React, { useState, useEffect } from 'react';

// Mantine
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import {
  useThemedLayoutContext,
  type RefineThemedLayoutV2HeaderProps,
} from '@refinedev/mantine';

// Auth
import { useIsAdmin } from 'hooks/useIsAdmin';

// Store
import { ALL_SITES_VALUE, useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';
import { useSiderStore } from 'store/SiderStore';
import { useSettingsStore } from 'store/SettingsStore';

// Hooks
import { useInitializeSite, useIsMobile, useIsProvider } from 'hooks';

//Interface
import { Organization, Site } from 'interfaces';

// Sentry
import * as Sentry from '@sentry/react';
import { MobileHeader } from './components/MobileHeader';
import { DesktopHeader } from './components/DesktopHeader';

export const Header: React.FC<RefineThemedLayoutV2HeaderProps> = () => {
  useHotkeys([['[', () => toggleSider(!isMobile)]]);
  const isAdmin = useIsAdmin();
  const isProvider = useIsProvider();
  const { isCollapsed, setIsCollapsed } = useSiderStore();
  const { setSiderCollapsed, setMobileSiderOpen, mobileSiderOpen } =
    useThemedLayoutContext();

  const computedColorScheme = useComputedColorScheme('dark');
  const { toggleColorScheme } = useMantineColorScheme();

  const isMobile = useIsMobile(700);

  const { defaultSiteID } = useSettingsStore();
  const { providerSiteSelection, selectAllProviderSites } =
    useCurrentSiteStore();
  const isAllSites = providerSiteSelection === 'all';
  const [selectedSiteID, setSelectedSiteID] = useState<string>(() =>
    isProvider && isAllSites ? ALL_SITES_VALUE : defaultSiteID || ''
  );
  const [selectedOrganizationID, setSelectedOrganizationID] =
    useState<string>('');
  const [sites, setSites] = useState<Site[]>([]);
  const [fetchedSites, setFetchedSites] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [fetchedOrganizations, setFetchedOrganizations] =
    useState<boolean>(false);

  useInitializeSite({
    sites,
    fetchedSites,
    setSelectedSiteID,
    setSelectedOrganizationID,
    selectedOrganizationID,
    organizations,
    fetchedOrganizations,
  });

  const resolveNextSite = (siteID?: string | null): Site | undefined => {
    if (siteID) {
      const match = sites.find((site) => site.id === siteID);
      if (match) return match;
    }

    if (defaultSiteID) {
      const defaultMatch = sites.find((site) => site.id === defaultSiteID);
      if (defaultMatch) return defaultMatch;
    }

    return sites[0];
  };

  const handleSiteChange = (siteID: string | null) => {
    if (isProvider && siteID === ALL_SITES_VALUE) {
      selectAllProviderSites();
      setSelectedSiteID(ALL_SITES_VALUE);
      Sentry.setTag('siteID', ALL_SITES_VALUE);
      Sentry.setTag('organizationID', ALL_SITES_VALUE);
      return;
    }

    const nextSite = resolveNextSite(siteID);

    if (!nextSite) {
      setSelectedSiteID('');
      return;
    }

    setSelectedSiteID(nextSite.id);

    const currentUserState = useCurrentUserStore.getState();
    useCurrentSiteStore.getState().setCurrentSite(nextSite);
    Sentry.setTag('siteID', nextSite.id);
    Sentry.setTag('organizationID', nextSite?.organizationID);
    if (selectedOrganizationID !== nextSite.organizationID) {
      setSelectedOrganizationID(nextSite.organizationID);
      useCurrentUserStore.setState({
        organizationID: nextSite.organizationID,
        ...currentUserState,
        organization: organizations.find(
          (org) => org.id === nextSite.organizationID
        ),
      });
    }
  };

  // Auto-select default site when sites load if none chosen yet
  useEffect(() => {
    if (!sites.length) return;

    if (isProvider && isAllSites) {
      if (selectedSiteID !== ALL_SITES_VALUE) {
        setSelectedSiteID(ALL_SITES_VALUE);
      }
      return;
    }

    const hasSelectedSite = selectedSiteID
      ? sites.some((s) => s.id === selectedSiteID)
      : false;

    if (!hasSelectedSite) {
      handleSiteChange(selectedSiteID || null);
    }
  }, [selectedSiteID, defaultSiteID, isAllSites, isProvider, sites]);

  const toggleSider = (isMobile: boolean) => {
    if (isMobile) {
      setSiderCollapsed(!isCollapsed);
      setIsCollapsed(!isCollapsed);
    } else {
      setMobileSiderOpen(!mobileSiderOpen);
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileHeader
          selectedSiteID={selectedSiteID}
          setSelectedSiteID={setSelectedSiteID}
          fetchedSites={fetchedSites}
          handleSiteChange={handleSiteChange}
          isAdmin={isAdmin}
          setFetchedOrganizations={setFetchedOrganizations}
          setFetchedSites={setFetchedSites}
          setOrganizations={setOrganizations}
          setSites={setSites}
          computedColorScheme={computedColorScheme}
          toggleColorScheme={toggleColorScheme}
          isProvider={isProvider}
        />
      ) : (
        <DesktopHeader
          isAdmin={isAdmin}
          isProvider={isProvider}
          selectedSiteID={selectedSiteID}
          handleSiteChange={handleSiteChange}
          setFetchedSites={setFetchedSites}
          setFetchedOrganizations={setFetchedOrganizations}
          setSites={setSites}
          setOrganizations={setOrganizations}
        />
      )}
    </>
  );
};
